import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

const range = (from, to, step = 1) => {
  let i = from;
  const range = [];

  while (i <= to) {
    range.push(i);
    i += step;
  }

  return range;
};

class Pagination extends Component {
  constructor(props) {
    super(props);

    this.pageLimit = 30;
    this.totalRecords = 0;
    this.pageNeighbours = 0;

    this.totalPages = Math.ceil(this.totalRecords / this.pageLimit);
    this.state = { currentPage: 1, hoveredPage: null };
    this.pageRefs = {};
  }

  componentDidMount() {
    this.getProps(this.props);
    this.gotoPage(1);
    this.updatePageStyles();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.hoveredPage !== this.state.hoveredPage || prevState.currentPage !== this.state.currentPage) {
      this.updatePageStyles();
    }
  }

  updatePageStyles = () => {
    const { currentPage, hoveredPage } = this.state;
    const pages = this.fetchPageNumbers();
    
    pages.forEach((page) => {
      if (typeof page === 'number' && this.pageRefs[page]) {
        const el = this.pageRefs[page];
        const isActive = currentPage === page;
        const isHovered = hoveredPage === page;
        const shouldShowHoverStyle = isHovered && !isActive;
        
        el.style.setProperty('border-radius', '100px', 'important');
        
        if (shouldShowHoverStyle) {
          el.style.setProperty('background-color', '#61E6BD', 'important');
          el.style.setProperty('border-color', '#61E6BD', 'important');
          el.style.setProperty('color', '#3E4754', 'important');
        } else if (isActive) {
          el.style.setProperty('background-color', '#61E6BD', 'important');
          el.style.setProperty('border-color', '#61E6BD', 'important');
          el.style.setProperty('color', '#3E4754', 'important');
        } else {
          el.style.setProperty('background-color', '#1F2937', 'important');
          el.style.setProperty('border-color', '#374151', 'important');
          el.style.setProperty('color', '#FFFFFF', 'important');
        }
      }
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.totalRecords !== nextProps.totalRecords) {
      this.getProps(nextProps);
      this.gotoPage(1);
    }
  }

  getProps = (props) => {
    const { totalRecords = null, pageLimit = 30, pageNeighbours = 0 } = props;

    this.pageLimit = typeof pageLimit === 'number' ? pageLimit : 30;
    this.totalRecords = typeof totalRecords === 'number' ? totalRecords : 0;

    this.pageNeighbours = typeof pageNeighbours === 'number' ? Math.max(0, Math.min(pageNeighbours, 2)) : 0;

    this.totalPages = Math.ceil(this.totalRecords / this.pageLimit);
  };

  gotoPage = (page) => {
    const { onPageChanged = (f) => f, type = 'default' } = this.props;

    const currentPage = Math.max(0, Math.min(page, this.totalPages));

    const paginationData = {
      currentPage,
      totalPages: this.totalPages,
      pageLimit: this.pageLimit,
      totalRecords: this.totalRecords,
      type,
    };

    this.setState({ currentPage }, () => {
      this.updatePageStyles();
      onPageChanged(paginationData);
    });
  };

  handleClick = (page, evt) => {
    evt.preventDefault();
    this.gotoPage(page);
  };

  handleMoveLeft = (evt) => {
    evt.preventDefault();
    this.gotoPage(this.state.currentPage - 1);
  };

  handleMoveRight = (evt) => {
    evt.preventDefault();
    this.gotoPage(this.state.currentPage + 1);
  };

  fetchPageNumbers = () => {
    const totalPages = this.totalPages;
    const currentPage = this.state.currentPage;
    const pageNeighbours = this.pageNeighbours;

    const totalNumbers = this.pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      let pages = [];

      const leftBound = currentPage - pageNeighbours;
      const rightBound = currentPage + pageNeighbours;
      const beforeLastPage = totalPages - 1;

      const startPage = leftBound > 2 ? leftBound : 2;
      const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;

      pages = range(startPage, endPage);

      const pagesCount = pages.length;
      const singleSpillOffset = totalNumbers - pagesCount - 1;

      const leftSpill = startPage > 2;
      const rightSpill = endPage < beforeLastPage;

      const leftSpillPage = LEFT_PAGE;
      const rightSpillPage = RIGHT_PAGE;

      if (leftSpill && !rightSpill) {
        const extraPages = range(startPage - singleSpillOffset, startPage - 1);
        pages = [leftSpillPage, ...extraPages, ...pages];
      } else if (!leftSpill && rightSpill) {
        const extraPages = range(endPage + 1, endPage + singleSpillOffset);
        pages = [...pages, ...extraPages, rightSpillPage];
      } else if (leftSpill && rightSpill) {
        pages = [leftSpillPage, ...pages, rightSpillPage];
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  render() {
    if (!this.totalRecords) return null;

    if (this.totalPages === 1) return null;

    const { currentPage } = this.state;
    const pages = this.fetchPageNumbers();

    return (
      <Fragment>
        <nav aria-label="Countries Pagination" className="pagination-wrapper">
          <ul className="pagination">
            {pages.map((page, index) => {
              const navLinkStyle = {
                padding: '1px 16px',
                fontSize: '18px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                minWidth: '48px',
              };

              if (page === LEFT_PAGE)
                return (
                  <li key={index} className="page-item">
                    {/* eslint-disable-next-line */}
                    <a className="page-link" href="#" aria-label="Previous" onClick={this.handleMoveLeft} style={navLinkStyle}>
                      <span aria-hidden="true">&laquo;</span>
                      <span className="sr-only">Previous</span>
                    </a>
                  </li>
                );

              if (page === RIGHT_PAGE)
                return (
                  <li key={index} className="page-item">
                    {/* eslint-disable-next-line */}
                    <a className="page-link" href="#" aria-label="Next" onClick={this.handleMoveRight} style={navLinkStyle}>
                      <span aria-hidden="true">&raquo;</span>
                      <span className="sr-only">Next</span>
                    </a>
                  </li>
                );

              const isActive = currentPage === page;
              
              return (
                <li key={index} className={`page-item${isActive ? ' active' : ''}`}>
                  {/* eslint-disable-next-line */}
                  <a className="page-link"
                    ref={(el) => {
                      if (el && typeof page === 'number') {
                        this.pageRefs[page] = el;
                        // Update style immediately when ref is set
                        setTimeout(() => this.updatePageStyles(), 0);
                      }
                    }}
                    onMouseEnter={() => this.setState({ hoveredPage: page })}
                    onMouseLeave={() => this.setState({ hoveredPage: null })}
                    style={{ 
                      padding: '1px 16px', 
                      fontSize: '18px', width: '48px', height: '48px', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      textDecoration: 'none', transition: 'all 0.3s ease', minWidth: '48px'
                    }}
                   href="#" onClick={(e) => this.handleClick(page, e)}>
                    {page}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </Fragment>
    );
  }
}

Pagination.propTypes = {
  totalRecords: PropTypes.number.isRequired,
  pageLimit: PropTypes.number,
  pageNeighbours: PropTypes.number,
  onPageChanged: PropTypes.func,
};

export default Pagination;
